import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Download, FileSpreadsheet, Users, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [loading, setLoading] = useState({ subscribers: false, feedback: false });
  const [stats, setStats] = useState({ subscribers: 0, feedback: 0 });

  const downloadCSV = async (type) => {
    setLoading({ ...loading, [type]: true });
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roastey_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to download CSV. Please try again.');
    } finally {
      setLoading({ ...loading, [type]: false });
    }
  };

  const loadStats = async () => {
    try {
      const [subsRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/newsletter/subscribers`),
        axios.get(`${API}/feedback`)
      ]);
      setStats({
        subscribers: subsRes.data.length,
        feedback: feedbackRes.data.length
      });
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  React.useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Fredoka, sans-serif'}}>
            Roastey Snacks Admin 📊
          </h1>
          <p className="text-gray-600">Export your customer data & feedback</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="border-2 border-orange-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-orange-100 to-yellow-100">
              <CardTitle className="flex items-center gap-3 text-gray-900">
                <Users className="w-6 h-6 text-orange-500" />
                Newsletter Subscribers
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-4xl font-bold text-orange-500 mb-4">
                {stats.subscribers}
              </div>
              <p className="text-gray-600 mb-4">Total subscribers for monthly health tips</p>
              <Button
                onClick={() => downloadCSV('subscribers')}
                disabled={loading.subscribers}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading.subscribers ? 'Downloading...' : 'Download Subscribers CSV'}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-orange-100 to-yellow-100">
              <CardTitle className="flex items-center gap-3 text-gray-900">
                <MessageSquare className="w-6 h-6 text-orange-500" />
                Customer Feedback
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-4xl font-bold text-orange-500 mb-4">
                {stats.feedback}
              </div>
              <p className="text-gray-600 mb-4">Product reviews and suggestions</p>
              <Button
                onClick={() => downloadCSV('feedback')}
                disabled={loading.feedback}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading.feedback ? 'Downloading...' : 'Download Feedback CSV'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Google Sheets Info */}
        <Card className="border-2 border-green-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-100 to-emerald-100">
            <CardTitle className="flex items-center gap-3 text-gray-900">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
              Google Sheets Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-gray-700">
                <strong>Auto-sync your data to Google Sheets in real-time!</strong>
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Setup Instructions:</strong><br/>
                  1. Your data is already being stored in MongoDB<br/>
                  2. Check <code className="bg-gray-100 px-2 py-1 rounded">/app/GOOGLE_SHEETS_SETUP.md</code> for integration steps<br/>
                  3. Follow the guide to connect your Google Sheet
                </p>
              </div>
              <p className="text-sm text-gray-600 italic">
                💡 Once connected, every new subscriber and feedback will automatically appear in your Google Sheet!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            ← Back to Landing Page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
